import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert, Box, Button, Chip, Container, Fade, Grid, IconButton,
  InputAdornment, LinearProgress, MenuItem, Paper, Select, Skeleton, Stack,
  TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography, keyframes,
} from "@mui/material";
import {
  ArrowForward, AutoAwesome, AccessTime, CheckCircle,
  Favorite, FavoriteBorder, LockOpen, LockOutlined, MenuBook, OndemandVideo,
  Pets, PlayCircleOutline, School, Search, ChatBubbleOutline,
  GridView, ViewList, TrendingUp, Star,
} from "@mui/icons-material";
import {
  MEMBERSHIP_COLORS, ContentContainer,
} from "../../components/membership/MembershipStyles";
import coursesService, { PublicCourseCard } from "../../services/coursesService";
import { apiClient } from "../../services/authService";
import { useLanguage } from "../../contexts/LanguageContext";
import UnlockCourseDialog, { UNLOCK_PLAN_ID, UNLOCK_PLAN_NAME } from "../../components/membership/UnlockCourseDialog";
import {
  VIDEO_ADDON_PER_COURSE, VIDEO_ADDON_PLAN_ID, VIDEO_ADDON_PLAN_NAME, computePlanPrice,
} from "../../utils/planPricing";
import {
  bilingualSearchText,
  localizedDescription,
  localizedTitle,
} from "../../utils/localizedContent";

// ─── Animations ────────────────────────────────────────────────
const fadeSlideUp = keyframes`
  from { opacity:0; transform:translateY(24px); }
  to   { opacity:1; transform:translateY(0); }
`;
const floatOrb = keyframes`
  0%,100% { transform:translateY(0) scale(1); }
  50%      { transform:translateY(-18px) scale(1.04); }
`;
const pulse = keyframes`
  0%,100% { opacity:1; }
  50%      { opacity:0.6; }
`;

// ─── Palette ───────────────────────────────────────────────────
const BLUE   = MEMBERSHIP_COLORS.blue;
const NAVY   = MEMBERSHIP_COLORS.navy;
const BG     = MEMBERSHIP_COLORS.bg;
const WHITE  = MEMBERSHIP_COLORS.white;
const MUTED  = MEMBERSHIP_COLORS.muted;
const BORDER = MEMBERSHIP_COLORS.border;
const HERO_FROM = "#E8F1FB";
const HERO_TO   = "#F5F8FC";

/** Whether the course has a companion video to sell alongside the leaflet. */
const hasCompanionVideo = (course: PublicCourseCard) => (course.companionVideoCount ?? 0) > 0;

// ─── Banner types ───────────────────────────────────────────────
interface PublicBanner {
  _id: string; title: string; subtitle?: string; imageUrl?: string;
  linkUrl?: string; linkLabel?: string; type: "image" | "text";
  bgColor?: string; textColor?: string; order: number;
}

// ─── BannerCarousel ────────────────────────────────────────────
const BannerCarousel: React.FC = () => {
  const navigate = useNavigate();
  const { isRTL } = useLanguage();
  const [banners, setBanners] = useState<PublicBanner[]>([]);
  const [index, setIndex]     = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    apiClient.get<{ banners: PublicBanner[] }>("/banners/public", { params: { page: "classes" } })
      .then(res => setBanners(res.data.banners || [])).catch(() => {});
  }, []);

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setIndex(i => (i + 1) % banners.length), 5000);
  }, [banners.length]);

  useEffect(() => {
    if (banners.length > 1) startTimer();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [banners.length, startTimer]);

  if (!banners.length) return null;
  const banner = banners[index];
  const isExternal = banner.linkUrl?.startsWith("http");
  const handleCta = () => {
    if (!banner.linkUrl) return;
    if (isExternal) window.open(banner.linkUrl, "_blank", "noopener,noreferrer");
    else navigate(banner.linkUrl);
  };

  return (
    <Box sx={{ mb: 3, borderRadius: "20px", overflow: "hidden", position: "relative" }}>
      <Box sx={{
        height: { xs: 160, sm: 200, md: 230 }, display: "flex", alignItems: "center",
        position: "relative", overflow: "hidden", borderRadius: "20px",
        ...(banner.type === "image" && banner.imageUrl
          ? { backgroundImage: `url(${banner.imageUrl})`, backgroundSize: "cover", backgroundPosition: "center" }
          : { background: `linear-gradient(135deg, ${BLUE}, #42A5F5)` }),
      }} onClick={banner.linkUrl && !banner.linkLabel ? handleCta : undefined}>
        {banner.type === "image" && banner.imageUrl && (
          <Box sx={{ position: "absolute", inset: 0,
            background: isRTL
              ? "linear-gradient(to left, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.3) 55%, transparent 100%)"
              : "linear-gradient(to right, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.3) 55%, transparent 100%)" }} />
        )}
        <Box sx={{ position: "relative", zIndex: 1, px: { xs: 2.5, md: 4 }, maxWidth: { xs: "100%", md: "65%" } }}>
          <Typography variant="h5" fontWeight={800}
            sx={{ color: banner.textColor || WHITE, fontSize: { xs: "1.1rem", sm: "1.35rem", md: "1.6rem" }, lineHeight: 1.25 }}>
            {banner.title}
          </Typography>
          {banner.subtitle && (
            <Typography variant="body2"
              sx={{ color: banner.textColor || WHITE, opacity: 0.88, mt: 0.6, fontSize: { xs: "0.8rem", md: "0.9rem" } }}>
              {banner.subtitle}
            </Typography>
          )}
          {banner.linkUrl && banner.linkLabel && (
            <Button variant="contained" size="small" onClick={handleCta}
              sx={{
                mt: 1.5, textTransform: "none", fontWeight: 700, borderRadius: "8px",
                bgcolor: "rgba(255,255,255,0.22)", backdropFilter: "blur(4px)",
                color: banner.textColor || WHITE, border: "1px solid rgba(255,255,255,0.35)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.35)" },
              }}>
              {banner.linkLabel}
            </Button>
          )}
        </Box>
      </Box>
      {banners.length > 1 && (
        <Box sx={{ position: "absolute", bottom: 10, right: 14, display: "flex", gap: "5px", zIndex: 2 }}>
          {banners.map((_, i) => (
            <Box key={i} onClick={() => { setIndex(i); startTimer(); }}
              sx={{
                width: i === index ? 18 : 7, height: 7, borderRadius: "4px",
                bgcolor: i === index ? WHITE : "rgba(255,255,255,0.5)",
                cursor: "pointer", transition: "all 0.3s ease",
              }} />
          ))}
        </Box>
      )}
    </Box>
  );
};


const CourseSkeletonCard: React.FC = () => (
  <Paper elevation={0} sx={{ borderRadius: "22px", overflow: "hidden", border: `1px solid ${BORDER}`, height: "100%" }}>
    <Skeleton variant="rectangular" height={210} sx={{ bgcolor: "rgba(21,101,192,0.06)" }} />
    <Box sx={{ p: 2.5 }}>
      <Skeleton variant="text" width="75%" height={34} sx={{ bgcolor: "rgba(21,101,192,0.06)" }} />
      <Skeleton variant="text" width="95%" sx={{ bgcolor: "rgba(21,101,192,0.06)" }} />
      <Skeleton variant="text" width="85%" sx={{ bgcolor: "rgba(21,101,192,0.06)" }} />
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Skeleton variant="rounded" width={80} height={24} sx={{ bgcolor: "rgba(21,101,192,0.06)" }} />
        <Skeleton variant="rounded" width={80} height={24} sx={{ bgcolor: "rgba(21,101,192,0.06)" }} />
      </Stack>
      <Skeleton variant="rounded" width="100%" height={40} sx={{ mt: 2.5, bgcolor: "rgba(21,101,192,0.06)" }} />
    </Box>
  </Paper>
);

// ─── Section Header ────────────────────────────────────────────
interface SectionHeaderProps {
  title: string;
  subtitle: string;
  accent: string;
  chip: React.ReactNode;
}
const SectionHeader: React.FC<SectionHeaderProps> = ({ title, subtitle, accent, chip }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1.5 }}>
    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
      <Box sx={{ width: 4, height: 36, borderRadius: "3px", background: accent, flexShrink: 0 }} />
      <Box>
        <Typography variant="h5" fontWeight={800} sx={{ color: NAVY, lineHeight: 1.2 }}>{title}</Typography>
        <Typography variant="body2" sx={{ color: MUTED, mt: 0.3 }}>{subtitle}</Typography>
      </Box>
    </Box>
    {chip}
  </Box>
);

// ─── Types ─────────────────────────────────────────────────────
type SortKey  = "newest" | "popular" | "lessons" | "videos";
type ViewMode = "grid"   | "list";

// ─── Main Component ────────────────────────────────────────────
const Classes: React.FC = () => {
  const navigate = useNavigate();
  const { t, isRTL, language } = useLanguage();
  const tc = t.classes;

  // Course text is stored per-language in the database; resolve against the
  // active locale with a fallback so partly-translated courses still render.
  const courseTitle = useCallback(
    (course: PublicCourseCard) => localizedTitle(course, language),
    [language]
  );
  const courseDescription = useCallback(
    (course: PublicCourseCard) => localizedDescription(course, language) || tc.fallbackDescription,
    [language, tc]
  );
  const formatDuration = useCallback(
    (seconds: number) => {
      if (!seconds || seconds < 1) return tc.quickStart;
      const m = Math.round(seconds / 60);
      if (m < 60) return tc.minutes(m);
      return tc.hoursMinutes(Math.floor(m / 60), m % 60);
    },
    [tc]
  );

  const [courses,         setCourses]         = useState<PublicCourseCard[]>([]);
  const [loading,         setLoading]         = useState(true);
  // Server message kept as-is; the fallback wording is translated on render.
  const [error,           setError]           = useState<{ message?: string } | null>(null);
  const [search,          setSearch]          = useState("");
  const [sort,            setSort]            = useState<SortKey>("popular");
  const [view,            setView]            = useState<ViewMode>("grid");
  const [likingCourseId,  setLikingCourseId]  = useState<string | null>(null);
  const [subscription,    setSubscription]    = useState<any>(null);
  const [allowedIds,      setAllowedIds]      = useState<string[] | null>(null);
  // Companion-video entitlement, merged across every purchase by the server.
  const [videoIds,        setVideoIds]        = useState<string[]>([]);
  const [allVideos,       setAllVideos]       = useState(false);
  const [unlockTarget,    setUnlockTarget]    = useState<PublicCourseCard | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [coursesRes, subRes] = await Promise.allSettled([
          coursesService.getCourses({ limit: 100 }),
          apiClient.get("/subscriptions/me"),
        ]);
        setCourses(coursesRes.status === "fulfilled" ? (coursesRes.value.courses || []) : []);
        if (subRes.status === "fulfilled") {
          const sub = subRes.value.data?.data;
          if (sub && sub.status === "active") {
            const MANUAL_METHODS = ["wish_money", "omt", "bank", "whatsapp"];
            const awaitingVerification =
              MANUAL_METHODS.includes(sub.paymentMethod) && sub.cancelReason !== "verified";
            if (awaitingVerification) {
              // Manual payment submitted but admin hasn't verified yet — show banner,
              // don't unlock any courses.
              setSubscription({ ...sub, _pendingVerification: true, plan: "" } as any);
            } else {
              setSubscription(sub);
              if (sub.selectedCourseIds?.length) {
                setAllowedIds(sub.selectedCourseIds.map((id: any) => String(id)));
              }
              setVideoIds((sub.videoCourseIds || []).map((id: any) => String(id)));
              setAllVideos(!!sub.allVideosIncluded);
            }
          }
        }
      } catch (err: any) {
        setError({ message: err?.response?.data?.message });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const isCourseUnlocked = useCallback((_courseId: string): boolean => {
    if (!subscription) return false;
    // "pro" (Elite) and "premium" (Pro) both get full access to all courses
    if (subscription.plan === "pro" || subscription.plan === "premium") return true;
    // "basic" (Starter) — limited to selectedCourseIds
    if (allowedIds && allowedIds.length > 0) return allowedIds.includes(_courseId);
    return false;
  }, [subscription, allowedIds]);

  const hasVideoAccess = useCallback(
    (courseId: string): boolean => allVideos || videoIds.includes(courseId),
    [allVideos, videoIds]
  );

  const hasSubscription = !!subscription;
  const isFullAccess = subscription?.plan === "pro" || subscription?.plan === "premium";

  const filteredCourses = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = q
      ? courses.filter(c =>
          // Match on both languages so a search works whichever the user types in.
          bilingualSearchText(c.title, c.titleAr, c.description, c.descriptionAr).includes(q)
        )
      : [...courses];
    switch (sort) {
      case "popular": list.sort((a, b) => (b.likeCount || 0) - (a.likeCount || 0)); break;
      case "lessons":  list.sort((a, b) => (b.itemCount  || 0) - (a.itemCount  || 0)); break;
      case "videos":   list.sort((a, b) => (b.videoCount || 0) - (a.videoCount || 0)); break;
    }
    return list;
  }, [courses, search, sort]);

  // Split into unlocked / locked sections
  const unlockedFiltered = useMemo(
    () => filteredCourses.filter(c => isCourseUnlocked(c._id)),
    [filteredCourses, isCourseUnlocked]
  );
  const lockedFiltered = useMemo(
    () => filteredCourses.filter(c => !isCourseUnlocked(c._id)),
    [filteredCourses, isCourseUnlocked]
  );

  const inProgress = useMemo(
    () => courses.filter((c: any) =>
      typeof c.progress === "number" && c.progress > 0 && c.progress < 100 && isCourseUnlocked(c._id)
    ),
    [courses, isCourseUnlocked]
  );

  const aggregateStats = useMemo(() =>
    courses.reduce((acc, c) => ({
      totalCourses:  acc.totalCourses  + 1,
      totalVideos:   acc.totalVideos   + c.videoCount,
      totalLessons:  acc.totalLessons  + c.itemCount,
    }), { totalCourses: 0, totalVideos: 0, totalLessons: 0 }),
    [courses]
  );

  const toggleCourseLike = async (courseId: string) => {
    if (likingCourseId) return;
    const target = courses.find(c => c._id === courseId);
    if (!target) return;
    const optimisticLiked = !target.likedByMe;
    const optimisticCount = Math.max(0, target.likeCount + (optimisticLiked ? 1 : -1));
    setCourses(prev => prev.map(c =>
      c._id === courseId ? { ...c, likedByMe: optimisticLiked, likeCount: optimisticCount } : c
    ));
    setLikingCourseId(courseId);
    try {
      const result = await coursesService.toggleCourseLike(courseId);
      setCourses(prev => prev.map(c =>
        c._id === courseId ? { ...c, likedByMe: result.likedByMe, likeCount: result.likeCount } : c
      ));
    } catch {
      setCourses(prev => prev.map(c =>
        c._id === courseId ? { ...c, likedByMe: target.likedByMe, likeCount: target.likeCount } : c
      ));
    } finally {
      setLikingCourseId(null);
    }
  };

  // Send the user to the payment page to pay for this single course. On a
  // successful payment the backend grants the course through their subscription,
  // so it shows up in their unlocked courses (selectedCourseIds).
  const goToUnlockPayment = (course: PublicCourseCard, withVideo: boolean) => {
    const videoCourseIds = withVideo ? [course._id] : [];
    const title = localizedTitle(course, language);
    navigate("/payment", {
      state: {
        planId: UNLOCK_PLAN_ID,
        planName: UNLOCK_PLAN_NAME,
        price: computePlanPrice({ planId: UNLOCK_PLAN_ID, videoCourseIds }),
        billing: "monthly",
        selectedCourseIds: [course._id],
        selectedCourseTitles: [title],
        videoCourseIds,
        videoCourseTitles: withVideo ? [title] : [],
      },
    });
  };

  // A course with a companion video gets a dialog to choose whether it comes
  // along; one without has nothing to choose, so it goes straight to checkout.
  const handleUnlockClick = (course: PublicCourseCard) => {
    if (hasCompanionVideo(course)) setUnlockTarget(course);
    else goToUnlockPayment(course, false);
  };

  // Buy just the companion video for a course the user already owns. The
  // server checks ownership before accepting, so this never unlocks a leaflet.
  const handleAddVideoClick = (course: PublicCourseCard) => {
    navigate("/payment", {
      state: {
        planId: VIDEO_ADDON_PLAN_ID,
        planName: VIDEO_ADDON_PLAN_NAME,
        price: computePlanPrice({ planId: VIDEO_ADDON_PLAN_ID, videoCourseIds: [course._id] }),
        billing: "one-time",
        selectedCourseIds: [],
        selectedCourseTitles: [],
        videoCourseIds: [course._id],
        videoCourseTitles: [localizedTitle(course, language)],
      },
    });
  };

  // Shown on an owned course: either that its video is included, or the
  // add-on to buy it. Nothing when the course has no companion video.
  const renderVideoAddon = (course: PublicCourseCard, compact: boolean) => {
    if (!hasCompanionVideo(course)) return null;
    if (hasVideoAccess(course._id)) {
      return (
        <Chip
          size="small"
          icon={<OndemandVideo sx={{ fontSize: "14px !important" }} />}
          label={tc.videoIncluded}
          sx={{
            alignSelf: compact ? "center" : "flex-start",
            bgcolor: "rgba(16,185,129,0.10)", color: "#059669", fontWeight: 700, fontSize: "0.72rem",
            border: "1px solid rgba(16,185,129,0.25)", "& .MuiChip-icon": { color: "#059669" },
          }}
        />
      );
    }
    return (
      <Tooltip title={tc.addVideoHint}>
        <Button
          fullWidth={!compact}
          size="small"
          variant="outlined"
          startIcon={<OndemandVideo sx={{ fontSize: "16px !important" }} />}
          onClick={e => { e.stopPropagation(); handleAddVideoClick(course); }}
          sx={{
            borderRadius: "12px", textTransform: "none", fontWeight: 700, whiteSpace: "nowrap",
            borderColor: "rgba(21,101,192,0.35)", color: BLUE,
            "&:hover": { borderColor: BLUE, bgcolor: "rgba(21,101,192,0.06)" },
          }}
        >
          {compact ? tc.addVideoShort(VIDEO_ADDON_PER_COURSE) : tc.addVideo(VIDEO_ADDON_PER_COURSE)}
        </Button>
      </Tooltip>
    );
  };

  // Plan label
  const planLabel = subscription?.plan === "pro" ? "Elite"
    : subscription?.plan === "premium" ? "Pro"
    : subscription?.plan === "basic" ? "Starter"
    : subscription?.plan ?? "";

  // ── Card renderers ────────────────────────────────────────────
  const renderUnlockedCard = (course: PublicCourseCard, idx: number) => (
    <Grid key={course._id} size={{ xs: 12, sm: 6, lg: 4 }}>
      <Fade in timeout={350 + idx * 70}>
        <Paper elevation={0} sx={{
          borderRadius: "22px", overflow: "hidden",
          border: `1px solid ${BORDER}`, bgcolor: WHITE,
          height: "100%", display: "flex", flexDirection: "column",
          transition: "transform 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease",
          "&:hover": {
            transform: "translateY(-6px)",
            boxShadow: "0 24px 48px rgba(21,101,192,0.14)",
            borderColor: "rgba(21,101,192,0.35)",
          },
        }}>
          <Box sx={{
            height: 220, position: "relative",
            background: course.thumbnail
              ? `linear-gradient(to top, rgba(10,22,40,0.35), rgba(10,22,40,0)), url(${course.thumbnail}) center/cover no-repeat`
              : `linear-gradient(135deg, rgba(21,101,192,0.15), rgba(66,165,245,0.1))`,
          }}>
            {!course.thumbnail && (
              <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <PlayCircleOutline sx={{ fontSize: 56, color: BLUE, opacity: 0.4 }} />
              </Box>
            )}
            <IconButton
              onClick={e => { e.stopPropagation(); toggleCourseLike(course._id); }}
              disabled={likingCourseId === course._id}
              size="small"
              sx={{ position: "absolute", top: 12, left: 12, bgcolor: "rgba(255,255,255,0.92)", "&:hover": { bgcolor: WHITE } }}
            >
              {course.likedByMe
                ? <Favorite sx={{ color: "#DC2626", fontSize: 18 }} />
                : <FavoriteBorder sx={{ color: NAVY, fontSize: 18 }} />}
            </IconButton>
            <Chip
              label={formatDuration(course.estimatedDuration)}
              size="small"
              icon={<AccessTime sx={{ fontSize: "14px !important" }} />}
              sx={{
                position: "absolute", top: 12, right: 12,
                bgcolor: "rgba(255,255,255,0.92)", fontWeight: 700, fontSize: "0.72rem", backdropFilter: "blur(4px)",
                "& .MuiChip-icon": { color: BLUE },
              }}
            />
          </Box>
          <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", flexGrow: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: NAVY, mb: 1, lineHeight: 1.3 }}>
              {courseTitle(course)}
            </Typography>
            <Typography sx={{ color: MUTED, mb: 2.2, lineHeight: 1.7, minHeight: 72, fontSize: "0.9rem" }}>
              {courseDescription(course)}
            </Typography>
            <Stack direction="row" spacing={0.8} sx={{ mb: 2.2, flexWrap: "wrap", rowGap: 0.8 }}>
              {[tc.phasesCount(course.phaseCount), tc.lessonsCount(course.itemCount), tc.videosCount(course.videoCount)].map(lbl => (
                <Chip key={lbl} label={lbl} size="small"
                  sx={{ bgcolor: "rgba(21,101,192,0.07)", color: BLUE, fontWeight: 600, fontSize: "0.7rem", border: `1px solid rgba(21,101,192,0.15)` }} />
              ))}
              <Chip icon={<ChatBubbleOutline sx={{ fontSize: "14px !important" }} />} label={`${course.commentCount}`} size="small"
                sx={{ bgcolor: "rgba(21,101,192,0.07)", color: BLUE, fontWeight: 600, fontSize: "0.7rem", border: `1px solid rgba(21,101,192,0.15)` }} />
              <Chip icon={<Star sx={{ fontSize: "14px !important" }} />} label={`${course.likeCount}`} size="small"
                sx={{ bgcolor: "rgba(21,101,192,0.07)", color: BLUE, fontWeight: 600, fontSize: "0.7rem", border: `1px solid rgba(21,101,192,0.15)` }} />
            </Stack>
            <Stack spacing={1} sx={{ mt: "auto" }}>
              <Tooltip title={tc.openDetails}>
                <Button fullWidth variant="contained"
                  endIcon={<ArrowForward sx={{ transform: isRTL ? "scaleX(-1)" : "none" }} />}
                  onClick={() => navigate(`/membership/classes/${course._id}`)}
                  sx={{
                    borderRadius: "12px", textTransform: "none",
                    fontWeight: 700, py: 1.15, bgcolor: BLUE,
                    boxShadow: "0 4px 14px rgba(21,101,192,0.3)",
                    "&:hover": { bgcolor: NAVY, boxShadow: "0 6px 20px rgba(21,101,192,0.4)" },
                  }}
                >
                  {tc.exploreButton}
                </Button>
              </Tooltip>
              {renderVideoAddon(course, false)}
            </Stack>
          </Box>
        </Paper>
      </Fade>
    </Grid>
  );

  const renderLockedCard = (course: PublicCourseCard, idx: number) => (
    <Grid key={course._id} size={{ xs: 12, sm: 6, lg: 4 }}>
      <Fade in timeout={350 + idx * 70}>
        <Paper elevation={0} sx={{
          borderRadius: "22px", overflow: "hidden",
          border: "1px solid rgba(0,0,0,0.07)", bgcolor: "#FAFAFA",
          height: "100%", display: "flex", flexDirection: "column",
          transition: "transform 0.28s ease, box-shadow 0.28s ease",
          "&:hover": { transform: "translateY(-3px)", boxShadow: "0 8px 24px rgba(0,0,0,0.07)" },
        }}>
          <Box sx={{ height: 220, position: "relative" }}>
            <Box sx={{
              position: "absolute", inset: 0,
              background: course.thumbnail
                ? `url(${course.thumbnail}) center/cover no-repeat`
                : `linear-gradient(135deg, rgba(21,101,192,0.15), rgba(66,165,245,0.1))`,
              filter: "grayscale(75%) brightness(0.7)",
            }} />
            <Box sx={{
              position: "absolute", inset: 0,
              bgcolor: "rgba(0,0,0,0.45)", backdropFilter: "blur(2px)",
              display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 1,
            }}>
              <Box sx={{
                width: 56, height: 56, borderRadius: "50%",
                bgcolor: "rgba(255,255,255,0.15)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <LockOutlined sx={{ color: WHITE, fontSize: 26 }} />
              </Box>
              <Typography sx={{ color: "rgba(255,255,255,.8)", fontSize: ".72rem", fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase" }}>
                {tc.locked}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", flexGrow: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#9CA3AF", mb: 1, lineHeight: 1.3 }}>
              {courseTitle(course)}
            </Typography>
            <Typography sx={{ color: "#B0B7C3", mb: 2.2, lineHeight: 1.7, minHeight: 72, fontSize: "0.9rem" }}>
              {courseDescription(course)}
            </Typography>
            <Stack direction="row" spacing={0.8} sx={{ mb: 2.2, flexWrap: "wrap", rowGap: 0.8 }}>
              {[tc.phasesCount(course.phaseCount), tc.lessonsCount(course.itemCount), tc.videosCount(course.videoCount)].map(lbl => (
                <Chip key={lbl} label={lbl} size="small"
                  sx={{ bgcolor: "rgba(0,0,0,0.04)", color: "#9CA3AF", fontWeight: 600, fontSize: "0.7rem", border: "1px solid rgba(0,0,0,0.07)" }} />
              ))}
            </Stack>
            <Button fullWidth variant="outlined" startIcon={<LockOutlined />}
              onClick={() => handleUnlockClick(course)}
              sx={{
                mt: "auto", borderRadius: "12px", textTransform: "none", fontWeight: 700, py: 1.15,
                borderColor: "rgba(0,0,0,0.15)", color: "#6B7280",
                "&:hover": { borderColor: BLUE, color: BLUE, bgcolor: "rgba(21,101,192,0.05)" },
              }}
            >
              {hasSubscription ? tc.unlockButton : tc.subscribeButton}
            </Button>
          </Box>
        </Paper>
      </Fade>
    </Grid>
  );

  const renderUnlockedListItem = (course: PublicCourseCard) => (
    <Paper key={course._id} elevation={0}
      onClick={() => navigate(`/membership/classes/${course._id}`)}
      sx={{
        p: 2, borderRadius: "18px", border: `1px solid ${BORDER}`,
        bgcolor: WHITE, cursor: "pointer", transition: "all .2s ease",
        "&:hover": { borderColor: BLUE, transform: "translateY(-2px)", boxShadow: "0 14px 30px rgba(21,101,192,0.10)" },
      }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <Box sx={{
          width: { xs: "100%", sm: 220 }, height: { xs: 160, sm: 130 }, flexShrink: 0, borderRadius: "12px",
          background: course.thumbnail ? `url(${course.thumbnail}) center/cover` : `linear-gradient(135deg, ${BLUE}, #42A5F5)`,
        }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, color: NAVY, fontSize: "1.05rem", mb: 0.5 }}>{courseTitle(course)}</Typography>
          <Typography sx={{
            color: MUTED, fontSize: "0.88rem", mb: 1.2, lineHeight: 1.6,
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
          }}>{courseDescription(course)}</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={0.8}>
            <Chip size="small" icon={<AccessTime sx={{ fontSize: "13px !important" }} />} label={formatDuration(course.estimatedDuration)}
              sx={{ bgcolor: "rgba(21,101,192,0.07)", color: BLUE, fontWeight: 600, fontSize: "0.7rem" }} />
            <Chip size="small" label={tc.lessonsCount(course.itemCount)}
              sx={{ bgcolor: "rgba(21,101,192,0.07)", color: BLUE, fontWeight: 600, fontSize: "0.7rem" }} />
            <Chip size="small" label={tc.videosCount(course.videoCount)}
              sx={{ bgcolor: "rgba(21,101,192,0.07)", color: BLUE, fontWeight: 600, fontSize: "0.7rem" }} />
            <Chip size="small" icon={<Star sx={{ fontSize: "13px !important" }} />} label={`${course.likeCount}`}
              sx={{ bgcolor: "rgba(21,101,192,0.07)", color: BLUE, fontWeight: 600, fontSize: "0.7rem" }} />
          </Stack>
        </Box>
        <Stack direction={{ xs: "row", sm: "column" }} spacing={1}
          sx={{ alignItems: { xs: "center", sm: "stretch" }, justifyContent: "center", flexShrink: 0, flexWrap: "wrap" }}>
          <Button variant="contained"
            endIcon={<ArrowForward sx={{ transform: isRTL ? "scaleX(-1)" : "none" }} />}
            sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 700, bgcolor: BLUE, "&:hover": { bgcolor: NAVY } }}>
            {t.common.view}
          </Button>
          {renderVideoAddon(course, true)}
        </Stack>
      </Stack>
    </Paper>
  );

  const renderLockedListItem = (course: PublicCourseCard) => (
    <Paper key={course._id} elevation={0}
      sx={{
        p: 2, borderRadius: "18px", border: "1px solid rgba(0,0,0,0.07)",
        bgcolor: "#FAFAFA", transition: "all .2s ease",
        "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.05)" },
      }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <Box sx={{ position: "relative", width: { xs: "100%", sm: 220 }, height: { xs: 160, sm: 130 }, flexShrink: 0, borderRadius: "12px", overflow: "hidden" }}>
          <Box sx={{
            position: "absolute", inset: 0,
            background: course.thumbnail ? `url(${course.thumbnail}) center/cover` : `linear-gradient(135deg, ${BLUE}, #42A5F5)`,
            filter: "grayscale(75%) brightness(0.7)",
          }} />
          <Box sx={{ position: "absolute", inset: 0, bgcolor: "rgba(0,0,0,0.42)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <LockOutlined sx={{ color: WHITE, fontSize: 28 }} />
          </Box>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, color: "#9CA3AF", fontSize: "1.05rem", mb: 0.5 }}>{courseTitle(course)}</Typography>
          <Typography sx={{
            color: "#B0B7C3", fontSize: "0.88rem", mb: 1.2, lineHeight: 1.6,
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
          }}>{courseDescription(course)}</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={0.8}>
            <Chip size="small" label={tc.lessonsCount(course.itemCount)}
              sx={{ bgcolor: "rgba(0,0,0,0.04)", color: "#9CA3AF", fontWeight: 600, fontSize: "0.7rem" }} />
            <Chip size="small" label={tc.videosCount(course.videoCount)}
              sx={{ bgcolor: "rgba(0,0,0,0.04)", color: "#9CA3AF", fontWeight: 600, fontSize: "0.7rem" }} />
          </Stack>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          <Button variant="outlined" startIcon={<LockOutlined />}
            onClick={e => { e.stopPropagation(); handleUnlockClick(course); }}
            sx={{
              borderRadius: "12px", textTransform: "none", fontWeight: 700,
              borderColor: "rgba(0,0,0,0.15)", color: "#6B7280",
              "&:hover": { borderColor: BLUE, color: BLUE, bgcolor: "rgba(21,101,192,0.05)" },
            }}>
            {hasSubscription ? tc.unlockShort : tc.subscribeShort}
          </Button>
        </Box>
      </Stack>
    </Paper>
  );

  return (
    <Box sx={{ backgroundColor: BG, minHeight: "100vh" }}>
      <UnlockCourseDialog
        open={!!unlockTarget}
        course={unlockTarget}
        onClose={() => setUnlockTarget(null)}
        onConfirm={withVideo => {
          const course = unlockTarget;
          setUnlockTarget(null);
          if (course) goToUnlockPayment(course, withVideo);
        }}
      />

      {/* ── Hero ──────────────────────────────────────────────── */}
      <Box sx={{
        position: "relative", overflow: "hidden",
        background: `linear-gradient(135deg, ${HERO_FROM} 0%, ${HERO_TO} 100%)`,
        borderBottom: `1px solid ${BORDER}`,
        pt: { xs: 6, md: 9 }, pb: { xs: 7, md: 9 },
      }}>
        <Box sx={{
          position: "absolute", inset: 0, opacity: 0.6,
          backgroundImage:
            `linear-gradient(rgba(21,101,192,.06) 1px, transparent 1px),
             linear-gradient(90deg, rgba(21,101,192,.06) 1px, transparent 1px)`,
          backgroundSize: "44px 44px",
          maskImage: "linear-gradient(180deg, black, transparent 85%)",
          WebkitMaskImage: "linear-gradient(180deg, black, transparent 85%)",
        }} />
        <Box sx={{
          position: "absolute", top: "-90px", right: "-70px", width: 360, height: 360, borderRadius: "50%",
          background: `radial-gradient(circle, rgba(21,101,192,0.18) 0%, transparent 70%)`,
          animation: `${floatOrb} 7s ease-in-out infinite`, pointerEvents: "none",
        }} />
        <Box sx={{
          position: "absolute", bottom: "-80px", left: "8%", width: 280, height: 280, borderRadius: "50%",
          background: `radial-gradient(circle, rgba(66,165,245,0.16) 0%, transparent 70%)`,
          animation: `${floatOrb} 9s ease-in-out infinite 1.5s`, pointerEvents: "none",
        }} />
        <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
          <Stack spacing={3}>
            <Box sx={{ animation: `${fadeSlideUp} .55s ease both` }}>
              <Chip
                icon={<AutoAwesome sx={{ fontSize: "15px !important", color: `${BLUE} !important` }} />}
                label={tc.chip}
                sx={{ bgcolor: "rgba(21,101,192,0.10)", color: BLUE, fontWeight: 700, border: "1px solid rgba(21,101,192,0.25)", backdropFilter: "blur(8px)" }}
              />
            </Box>
            <Box sx={{ animation: `${fadeSlideUp} .55s .1s ease both` }}>
              <Typography variant="h3" sx={{
                fontWeight: 900, color: NAVY, letterSpacing: "-0.04em",
                fontSize: { xs: "2rem", md: "3.2rem" }, maxWidth: 820, lineHeight: 1.15,
              }}>
                {tc.title}{" "}
                <Box component="span" sx={{
                  background: `linear-gradient(90deg, ${BLUE}, #42A5F5)`,
                  WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                }}>
                  {tc.titleHighlight}
                </Box>
              </Typography>
            </Box>
            <Box sx={{ animation: `${fadeSlideUp} .55s .2s ease both` }}>
              <Typography variant="h6" sx={{
                color: MUTED, fontWeight: 400, maxWidth: 680,
                lineHeight: 1.7, fontSize: { xs: "1rem", md: "1.08rem" },
              }}>
                {tc.subtitle}
              </Typography>
            </Box>
            <Box sx={{ animation: `${fadeSlideUp} .55s .3s ease both` }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                {[
                  { icon: <School sx={{ fontSize: 16 }} />,       label: tc.totalCourses(aggregateStats.totalCourses) },
                  { icon: <OndemandVideo sx={{ fontSize: 16 }} />, label: tc.totalVideos(aggregateStats.totalVideos) },
                  { icon: <MenuBook sx={{ fontSize: 16 }} />,      label: tc.totalLessons(aggregateStats.totalLessons) },
                ].map(item => (
                  <Chip key={item.label} icon={item.icon} label={item.label}
                    sx={{ bgcolor: WHITE, color: NAVY, border: `1px solid ${BORDER}`, fontWeight: 600,
                          boxShadow: "0 1px 4px rgba(10,22,40,0.04)", "& .MuiChip-icon": { color: BLUE } }}
                  />
                ))}
              </Stack>
            </Box>
            <Box sx={{ position: "absolute", top: 20, right: { xs: 20, md: 80 }, opacity: 0.10, pointerEvents: "none" }}>
              <Pets sx={{ fontSize: 80, color: BLUE }} />
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* ── Content ────────────────────────────────────────────── */}
      <ContentContainer maxWidth="xl">

        {/* ── Subscription banner ─────────────────────────────── */}
        {!loading && !isFullAccess && (
          (subscription as any)?._pendingVerification ? (
            /* Manual payment submitted — awaiting admin verification */
            <Paper elevation={0} sx={{
              p: { xs: 2, md: 2.5 }, mb: 3, borderRadius: "16px",
              background: "linear-gradient(135deg, #FFF8E1 0%, #FFFDE7 100%)",
              border: "1px solid #FFD54F",
            }}>
              <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }}
                justifyContent="space-between" spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: "10px", flexShrink: 0,
                    bgcolor: "#FFF3E0", display: "flex", alignItems: "center", justifyContent: "center",
                    animation: `${pulse} 2s ease-in-out infinite`,
                  }}>
                    <AccessTime sx={{ color: "#F57C00", fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography fontWeight={800} sx={{ color: "#E65100" }}>
                      Payment Under Review
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#BF360C" }}>
                      Our team will verify your payment and activate your access within 24 hours.
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Paper>
          ) : !hasSubscription ? (
            <Paper elevation={0} sx={{
              p: { xs: 2, md: 2.5 }, mb: 3, borderRadius: "16px",
              background: `linear-gradient(135deg, ${NAVY} 0%, #0D2137 100%)`,
              border: "1px solid rgba(66,165,245,.2)",
            }}>
              <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }}
                justifyContent="space-between" spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: "10px", flexShrink: 0,
                    bgcolor: "rgba(255,255,255,.1)", display: "flex", alignItems: "center", justifyContent: "center",
                    animation: `${pulse} 2s ease-in-out infinite`,
                  }}>
                    <LockOutlined sx={{ color: "#90CAF9", fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography fontWeight={800} sx={{ color: "#fff" }}>{tc.subscribeTitle}</Typography>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,.6)" }}>
                      {tc.subscribeSubtitle}
                    </Typography>
                  </Box>
                </Stack>
                <Button variant="contained"
                  endIcon={<ArrowForward sx={{ transform: isRTL ? "scaleX(-1)" : "none" }} />}
                  onClick={() => navigate("/")}
                  sx={{
                    textTransform: "none", fontWeight: 700, borderRadius: "50px",
                    px: 3, py: 1, bgcolor: BLUE, whiteSpace: "nowrap",
                    boxShadow: "0 4px 16px rgba(21,101,192,.4)", "&:hover": { bgcolor: "#1976D2" },
                  }}>
                  {tc.viewPlans}
                </Button>
              </Stack>
            </Paper>
          ) : (
            <Paper elevation={0} sx={{
              p: { xs: 2, md: 2.5 }, mb: 3, borderRadius: "16px",
              bgcolor: WHITE, border: `1px solid ${BORDER}`,
              boxShadow: "0 2px 12px rgba(21,101,192,.04)",
            }}>
              <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }}
                justifyContent="space-between" spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: "10px", flexShrink: 0,
                    background: `linear-gradient(135deg, ${BLUE}, #42A5F5)`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <LockOpen sx={{ color: WHITE, fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography fontWeight={800} sx={{ color: NAVY }}>
                      {planLabel} Plan — {allowedIds?.length ?? 0} course{(allowedIds?.length ?? 0) !== 1 ? "s" : ""} unlocked
                    </Typography>
                    <Typography variant="caption" sx={{ color: MUTED }}>
                      {tc.unlockHint}
                    </Typography>
                  </Box>
                </Stack>
                <Chip label="Active" size="small"
                  sx={{ bgcolor: "#ECFDF5", color: "#10B981", fontWeight: 700, border: "1px solid #A7F3D0" }} />
              </Stack>
            </Paper>
          )
        )}

        <BannerCarousel />

        {/* ── Continue Learning ──────────────────────────────── */}
        {inProgress.length > 0 && (
          <Paper elevation={0} sx={{
            p: { xs: 2, md: 3 }, mb: 3, borderRadius: "20px",
            border: `1px solid ${BORDER}`, bgcolor: WHITE,
          }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <TrendingUp sx={{ color: BLUE }} />
              <Typography sx={{ fontWeight: 800, color: NAVY, fontSize: "1.05rem" }}>{tc.continuelearning}</Typography>
            </Stack>
            <Grid container spacing={2}>
              {inProgress.slice(0, 3).map((c: any) => (
                <Grid key={c._id} size={{ xs: 12, md: 4 }}>
                  <Paper variant="outlined"
                    onClick={() => navigate(`/membership/classes/${c._id}`)}
                    sx={{ p: 1.5, borderRadius: "14px", borderColor: BORDER, cursor: "pointer", transition: "all .2s ease",
                      "&:hover": { borderColor: BLUE, transform: "translateY(-2px)" } }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box sx={{
                        width: 56, height: 56, borderRadius: "10px", flexShrink: 0,
                        background: c.thumbnail ? `url(${c.thumbnail}) center/cover` : `linear-gradient(135deg, ${BLUE}, #42A5F5)`,
                      }} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography noWrap sx={{ fontWeight: 700, color: NAVY, fontSize: "0.9rem" }}>{localizedTitle(c, language)}</Typography>
                        <LinearProgress variant="determinate" value={c.progress}
                          sx={{ mt: 0.8, height: 6, borderRadius: 3, bgcolor: "rgba(21,101,192,0.1)",
                                "& .MuiLinearProgress-bar": { bgcolor: BLUE, borderRadius: 3 } }} />
                        <Typography sx={{ mt: 0.5, fontSize: "0.72rem", color: MUTED, fontWeight: 600 }}>{tc.percentComplete(c.progress)}</Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {/* ── Toolbar ───────────────────────────────────────── */}
        <Paper elevation={0} sx={{
          p: 1.5, mb: 4, borderRadius: "16px", border: `1px solid ${BORDER}`, bgcolor: WHITE,
        }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
            <TextField
              fullWidth placeholder={tc.search}
              value={search} onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search sx={{ color: BLUE }} /></InputAdornment>,
                sx: {
                  borderRadius: "12px",
                  "& fieldset": { borderColor: BORDER },
                  "&:hover fieldset": { borderColor: `rgba(21,101,192,0.4) !important` },
                  "&.Mui-focused fieldset": { borderColor: `${BLUE} !important` },
                },
              }}
            />
            <Select value={sort} onChange={e => setSort(e.target.value as SortKey)} size="small"
              sx={{ borderRadius: "12px", minWidth: 160, "& fieldset": { borderColor: BORDER },
                    "&:hover fieldset": { borderColor: `rgba(21,101,192,0.4) !important` },
                    "&.Mui-focused fieldset": { borderColor: `${BLUE} !important` } }}>
              <MenuItem value="popular">{tc.sortMostPopular}</MenuItem>
              <MenuItem value="newest">{tc.sortNewest}</MenuItem>
              <MenuItem value="lessons">{tc.sortMostLessons}</MenuItem>
              <MenuItem value="videos">{tc.sortMostVideos}</MenuItem>
            </Select>
            <ToggleButtonGroup exclusive value={view} size="small" onChange={(_, v) => v && setView(v)}
              sx={{ "& .MuiToggleButton-root": { borderColor: BORDER, color: MUTED,
                    "&.Mui-selected": { bgcolor: BLUE, color: WHITE, "&:hover": { bgcolor: NAVY } } } }}>
              <ToggleButton value="grid"><GridView fontSize="small" /></ToggleButton>
              <ToggleButton value="list"><ViewList fontSize="small" /></ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error.message || tc.loadFailed}</Alert>}

        {loading ? (
          <Grid container spacing={3}>
            {[1, 2, 3, 4, 5, 6].map(n => (
              <Grid key={n} size={{ xs: 12, sm: 6, lg: 4 }}><CourseSkeletonCard /></Grid>
            ))}
          </Grid>
        ) : unlockedFiltered.length === 0 && lockedFiltered.length === 0 ? (
          /* ── No search results ── */
          <Paper elevation={0} sx={{ p: 6, borderRadius: "24px", border: `1px solid ${BORDER}`, textAlign: "center", bgcolor: WHITE }}>
            <Box sx={{
              width: 72, height: 72, borderRadius: "50%",
              background: `linear-gradient(135deg, ${BLUE}, #42A5F5)`,
              display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2.5,
            }}>
              <Search sx={{ color: WHITE, fontSize: 32 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: NAVY, mb: 1 }}>{tc.noResults}</Typography>
            <Typography sx={{ color: MUTED, mb: 3 }}>{tc.noResultsSubtitle}</Typography>
            <Button variant="contained" onClick={() => setSearch("")}
              sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 700, px: 3, py: 1.2, bgcolor: BLUE, "&:hover": { bgcolor: NAVY } }}>
              {tc.resetSearch}
            </Button>
          </Paper>
        ) : (
          <>
            {/* ════════════════════════════════════════════════
                MY COURSES — unlocked section
            ════════════════════════════════════════════════ */}
            {unlockedFiltered.length > 0 && (
              <Box sx={{ mb: lockedFiltered.length > 0 ? 6 : 0 }}>
                <SectionHeader
                  title={tc.myCourses}
                  subtitle={tc.myCoursesSubtitle(unlockedFiltered.length)}
                  accent={`linear-gradient(180deg, ${BLUE}, #42A5F5)`}
                  chip={
                    <Chip
                      icon={<CheckCircle sx={{ fontSize: "16px !important" }} />}
                      label={tc.unlocked}
                      sx={{ bgcolor: "#ECFDF5", color: "#10B981", fontWeight: 700, border: "1px solid #A7F3D0",
                            "& .MuiChip-icon": { color: "#10B981" } }}
                    />
                  }
                />
                {view === "grid" ? (
                  <Grid container spacing={3}>
                    {unlockedFiltered.map((course, idx) => renderUnlockedCard(course, idx))}
                  </Grid>
                ) : (
                  <Stack spacing={2}>
                    {unlockedFiltered.map(course => renderUnlockedListItem(course))}
                  </Stack>
                )}
              </Box>
            )}

            {/* ════════════════════════════════════════════════
                DISCOVER MORE — locked section
            ════════════════════════════════════════════════ */}
            {lockedFiltered.length > 0 && (
              <Box>
                {/* Divider between sections */}
                {unlockedFiltered.length > 0 && (
                  <Box sx={{ mb: 4, display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ flex: 1, height: "1px", bgcolor: BORDER }} />
                    <Chip label={tc.moreAvailable} size="small"
                      sx={{ bgcolor: "#F9FAFB", color: "#6B7280", fontWeight: 600, border: "1px solid #E5E7EB", fontSize: ".72rem" }} />
                    <Box sx={{ flex: 1, height: "1px", bgcolor: BORDER }} />
                  </Box>
                )}

                <SectionHeader
                  title={hasSubscription ? tc.discoverMore : tc.browseCourses}
                  subtitle={
                    hasSubscription
                      ? tc.discoverSubtitle(lockedFiltered.length)
                      : tc.browseSubtitle(lockedFiltered.length)
                  }
                  accent="linear-gradient(180deg, #9CA3AF, #D1D5DB)"
                  chip={
                    <Chip
                      icon={<LockOutlined sx={{ fontSize: "16px !important" }} />}
                      label={tc.locked}
                      sx={{ bgcolor: "#F9FAFB", color: "#6B7280", fontWeight: 700, border: "1px solid #E5E7EB",
                            "& .MuiChip-icon": { color: "#9CA3AF" } }}
                    />
                  }
                />

                {view === "grid" ? (
                  <Grid container spacing={3}>
                    {lockedFiltered.map((course, idx) => renderLockedCard(course, idx))}
                  </Grid>
                ) : (
                  <Stack spacing={2}>
                    {lockedFiltered.map(course => renderLockedListItem(course))}
                  </Stack>
                )}
              </Box>
            )}
          </>
        )}
      </ContentContainer>
    </Box>
  );
};

export default Classes;
